import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { refreshCachedSession, type StoredSession } from './session';

interface RequireAuthProps {
  children: React.ReactNode;
}

/**
 * Guard component. Reads the current session from IndexedDB on mount;
 * while loading, renders nothing to avoid a flash of protected content;
 * if the session is absent or expired, redirects to `/auth` with a
 * `returnTo` query param so the user lands back where they were.
 *
 * Component tests that render a screen directly (not via the root
 * `<App />`) bypass this guard entirely — they mount the screen
 * component itself, not the route tree.
 */
export default function RequireAuth({ children }: RequireAuthProps) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'signed-in'; session: StoredSession } | { status: 'signed-out' }
  >({ status: 'loading' });
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    // `refreshCachedSession` primes the synchronous session cache that
    // `apiClient.apiFetch` peeks at when attaching the `Authorization:
    // Bearer` header (Blocker-1 fix).
    void refreshCachedSession().then((session) => {
      if (cancelled) return;
      if (session) setState({ status: 'signed-in', session });
      else setState({ status: 'signed-out' });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') return null;
  if (state.status === 'signed-out') {
    const returnTo = `${location.pathname}${location.search}`;
    const q = returnTo && returnTo !== '/auth' ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    return <Navigate to={`/auth${q}`} replace />;
  }
  return <>{children}</>;
}
